import { useMemo } from 'react';
import type { OrgCapability } from '@shopgnd/shared';
import { useAuthStore } from '@/stores/authStore';
import { useVendorProfile } from '@/hooks/useVendorForUser';
import { useProProfile } from '@/hooks/useProForUser';
import { useWorkspace } from '@/hooks/useWorkspace';
import { impersonationHubFlags, useAdminImpersonation } from '@/lib/adminImpersonation';
import { hubEntryPath } from '@/lib/hubLanding';
import {
  defaultHubPathForAccess,
  hasHubAccessFromProfiles,
  type HubAccessFlags,
} from '@/lib/hubNavigation';

const SELLER_CAPS: OrgCapability[] = ['shop', 'classifieds', 'property', 'payouts'];

function orgHasSellerSurface(capabilities: OrgCapability[]): boolean {
  return capabilities.some((c) => SELLER_CAPS.includes(c));
}

export function useHubAccess(): HubAccessFlags & {
  ready: boolean;
  hasAnyHub: boolean;
  isPlatformAdmin: boolean;
  defaultPath: string;
} {
  const user = useAuthStore((s) => s.user);
  const roles = user?.roles ?? [];
  const { vendor, ready: vendorReady } = useVendorProfile();
  const { pro, ready: proReady } = useProProfile();
  const { org, ready: wsReady } = useWorkspace();

  const isPlatformAdmin = roles.includes('platform_admin');
  const { session: impersonationSession, impersonating } = useAdminImpersonation();
  const impersonationFlags = impersonationHubFlags(impersonationSession);
  const caps = org?.capabilities ?? [];

  const flags = useMemo<HubAccessFlags>(
    () => ({
      hasVendorHub:
        Boolean(vendor) ||
        (Boolean(org) && orgHasSellerSurface(caps)) ||
        impersonationFlags.hasVendorHub,
      hasProHub:
        Boolean(pro) ||
        (Boolean(org) && caps.includes('bookings')) ||
        impersonationFlags.hasProHub,
      hasAdminHub:
        !impersonating && (roles.includes('admin') || isPlatformAdmin),
    }),
    [vendor, pro, org, caps, roles, isPlatformAdmin, impersonating, impersonationFlags],
  );

  const ready = vendorReady && proReady && wsReady;

  const defaultPath = useMemo(() => {
    if (!ready) return defaultHubPathForAccess(flags);
    return hubEntryPath(flags, org, {
      vendorId: vendor?.id,
      proId: pro?.id,
    });
  }, [flags, org, ready, vendor?.id, pro?.id]);

  return useMemo(
    () => ({
      ...flags,
      ready,
      hasAnyHub: hasHubAccessFromProfiles(flags),
      isPlatformAdmin,
      defaultPath,
    }),
    [flags, ready, isPlatformAdmin, defaultPath],
  );
}
